import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import { useState } from 'react';

import { Button } from '../../cdl/Button/Button';
import { IconButton } from '../../cdl/IconButton/IconButton';
import { CenteredPagination } from '../../cdl/Pagination/CenteredPagination';
import { StatePlaceholder } from '../../cdl/StatePlaceholder/StatePlaceholder';
import { TableBuilder } from '../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../cdl/TableBuilder/TableBuilderColumn';
import { Text } from '../../cdl/Text/Text';
import { queries } from '../../common/api/queryKeys/queries';
import { AdvancedError } from '../../common/Error/AdvancedError';
import { CustomerGroupSelect } from '../../common/form-elements/CustomerGroupSelect/CustomerGroupSelect';
import { translate } from '../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../common/hooks/useDocumentTitle';
import { useRole } from '../../common/hooks/useRole';
import { IconGameplan } from '../../common/icons/cdl/Gameplan';
import { IconPencil } from '../../common/icons/cdl/Pencil';
import { IconPlus } from '../../common/icons/cdl/Plus';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { Page } from '../../common/Page/Page';
import { Box } from '../../common/ui/Box';
import { DemandDefinitionReferencePeriod } from '../components/DemandDefinitionReferencePeriod';
import { toLocalCalendarDate } from '../toLocalCalendarDate';
import { DemandDefinitionReferencePeriodFormat } from '../types/DemandDefinitionReferencePeriodFormat';
import { DemandDefinitionSubstep } from '../types/DemandDefinitionSubstep';
import type { DemandDefinitionSummary } from '../types/DemandDefinitionSummary';

interface DemandDefinitionSummaryRow extends Omit<DemandDefinitionSummary, 'referencePeriod'> {
    referencePeriod: {
        from: Date;
        to: Date;
    };
}

export const DemandDefinitionsPage = () => {
    useDocumentTitle(translate('demandDefinition.demandDefinitions'));
    const navigate = useNavigate({ from: '/demand-definitions/' });
    const role = useRole();
    const search = useSearch({ from: '/_app/demand-definitions' });
    const [currentPage, setCurrentPage] = useState(1);
    const isSuperAdmin = role.isSuperAdmin();
    const customerGroupId = isSuperAdmin ? search.customerGroupId : undefined;
    const isCustomerGroupMissing = isSuperAdmin && !customerGroupId;
    const definitionsQuery = useQuery({
        ...queries.demandDefinitions.list({
            page: currentPage - 1,
            customerGroupId,
        }),
        enabled: !isCustomerGroupMissing,
        select: (page) => ({
            ...page,
            content: page.content.map(
                (definition): DemandDefinitionSummaryRow => ({
                    ...definition,
                    referencePeriod: {
                        from: toLocalCalendarDate(definition.referencePeriod.from),
                        to: toLocalCalendarDate(definition.referencePeriod.to),
                    },
                })
            ),
        }),
    });

    const openCreateFlow = () => {
        navigate({
            to: '/demand-definitions/create/$substep',
            params: { substep: DemandDefinitionSubstep.GENERAL },
            search: { customerGroupId: undefined },
        });
    };

    return (
        <Page>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={24} marginBottom={32}>
                <Box display="flex" flexDirection="column" gap={4}>
                    <Text as="h1" variant="headline">
                        {translate('demandDefinition.demandDefinitions')}
                    </Text>
                    <Text variant="body" color="foreground.subtle">
                        {translate('demandDefinition.demandDefinitionsDescription')}
                    </Text>
                </Box>

                <Button emphasis="high" LeadingIcon={IconPlus} onClick={openCreateFlow}>
                    {translate('demandDefinition.createDemandDefinition')}
                </Button>
            </Box>

            {isSuperAdmin ? (
                <Box width="320px" marginBottom={24}>
                    <CustomerGroupSelect
                        label={translate('customerGroupSelect.label')}
                        placeholder={translate('customerGroupSelect.placeholder')}
                        value={customerGroupId ?? null}
                        isClearable
                        onChange={(nextCustomerGroupId) => {
                            setCurrentPage(1);
                            navigate({
                                search: (previousSearch) => ({
                                    ...previousSearch,
                                    customerGroupId: nextCustomerGroupId ?? undefined,
                                }),
                            });
                        }}
                    />
                </Box>
            ) : null}

            {isCustomerGroupMissing ? (
                <Box display="flex" justifyContent="center" paddingTop={88}>
                    <StatePlaceholder
                        title={translate('demandDefinition.selectCustomerGroup')}
                        description={translate(
                            'demandDefinition.chooseCustomerGroupAboveToViewDemandDefinitionsDescription'
                        )}
                        Icon={IconGameplan}
                    />
                </Box>
            ) : null}

            {!isCustomerGroupMissing && definitionsQuery.isPending ? <LoadingIndicator variant="page" /> : null}

            {!isCustomerGroupMissing && definitionsQuery.isError ? (
                <AdvancedError error={definitionsQuery.error} />
            ) : null}

            {!isCustomerGroupMissing && definitionsQuery.isSuccess && definitionsQuery.data.content.length === 0 ? (
                <Box display="flex" justifyContent="center" paddingTop={88}>
                    <StatePlaceholder
                        title={translate('demandDefinition.noDemandDefinitions')}
                        description={translate('demandDefinition.noDemandDefinitionsDescription')}
                        Icon={IconGameplan}
                        action={
                            <Button emphasis="high" LeadingIcon={IconPlus} onClick={openCreateFlow}>
                                {translate('demandDefinition.createDemandDefinition')}
                            </Button>
                        }
                    />
                </Box>
            ) : null}

            {!isCustomerGroupMissing && definitionsQuery.isSuccess && definitionsQuery.data.content.length > 0 ? (
                <>
                    <TableBuilder<DemandDefinitionSummaryRow> data={definitionsQuery.data.content}>
                        <TableBuilderColumn<DemandDefinitionSummaryRow> header={translate('demandDefinition.name')}>
                            {(definition) => definition.name}
                        </TableBuilderColumn>
                        <TableBuilderColumn<DemandDefinitionSummaryRow>
                            header={translate('demandDefinition.referencePeriod')}
                        >
                            {(definition) => (
                                <DemandDefinitionReferencePeriod
                                    formatVariant={DemandDefinitionReferencePeriodFormat.DATE}
                                    referencePeriod={definition.referencePeriod}
                                />
                            )}
                        </TableBuilderColumn>
                        <TableBuilderColumn<DemandDefinitionSummaryRow>
                            header={translate('demandDefinition.lastUpdated')}
                            numeric
                        >
                            {(definition) =>
                                definition.dateUpdated ? format(parseISO(definition.dateUpdated), 'MMM d, yyyy') : null
                            }
                        </TableBuilderColumn>
                        <TableBuilderColumn<DemandDefinitionSummaryRow> header="" interactive width="64px" numeric>
                            {(definition) => (
                                <Box display="flex" justifyContent="flex-end">
                                    <IconButton
                                        size="small"
                                        Icon={IconPencil}
                                        tooltipLabel={translate('demandDefinition.edit')}
                                        onClick={() => {
                                            navigate({
                                                to: '/demand-definitions/edit/$definitionId/$substep',
                                                params: {
                                                    definitionId: definition.id,
                                                    substep: DemandDefinitionSubstep.GENERAL,
                                                },
                                                search: { customerGroupId: undefined },
                                            });
                                        }}
                                    />
                                </Box>
                            )}
                        </TableBuilderColumn>
                    </TableBuilder>
                    <CenteredPagination
                        currentPage={currentPage}
                        pageTotal={Math.ceil(definitionsQuery.data.total / definitionsQuery.data.pageable.size)}
                        onPageChange={setCurrentPage}
                    />
                </>
            ) : null}
        </Page>
    );
};
